import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getAbarytDate,
  getTimeZoneReference,
  NATION_REFERENCE,
  NATION_REFERENCE_TIME_ZONE,
} from "../lib/abarytCalendar";

const Context = createContext(null);
const LEGACY_LOCATION_STORAGE_KEY = "ysq_abaryt_location";
const LOCATION_PROMPT_COUNT_KEY = "ysq_location_prompt_count";
const MAX_AUTO_PROMPTS_PER_SESSION = 2;

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function promptCount() {
  try {
    return Number(window.sessionStorage.getItem(LOCATION_PROMPT_COUNT_KEY) || "0");
  } catch {
    return 0;
  }
}

export function AbarytDateProvider({ children }) {
  const [now, setNow] = useState(() => new Date());
  const [position, setPosition] = useState(undefined);
  const [timeZone] = useState(() => browserTimeZone());
  const [status, setStatus] = useState(() =>
    promptCount() >= MAX_AUTO_PROMPTS_PER_SESSION ? "timezone" : "needs-consent"
  );

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_LOCATION_STORAGE_KEY);
      window.sessionStorage.removeItem(LEGACY_LOCATION_STORAGE_KEY);
    } catch {
      // No coordinates are written by this implementation.
    }
  }, []);

  useEffect(() => {
    if (status !== "needs-consent") return;
    try {
      const nextCount = Math.min(MAX_AUTO_PROMPTS_PER_SESSION, promptCount() + 1);
      window.sessionStorage.setItem(LOCATION_PROMPT_COUNT_KEY, String(nextCount));
    } catch {
      // Prompt limiting still works for the current mounted session.
    }
  }, [status]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setPosition(null);
      setStatus(timeZone ? "timezone" : "reference");
      return;
    }

    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setPosition({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
        setStatus("ready");
      },
      () => {
        setPosition(null);
        setStatus(timeZone ? "timezone" : "reference");
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 0,
      }
    );
  }, [timeZone]);

  const continueWithoutLocation = useCallback(() => {
    setPosition(null);
    setStatus(timeZone ? "timezone" : "reference");
  }, [timeZone]);

  useEffect(() => {
    const update = () => setNow(new Date());
    const timer = window.setInterval(update, 15000);
    window.addEventListener("focus", update);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", update);
    };
  }, []);

  const timeZoneReference = useMemo(
    () => (timeZone ? getTimeZoneReference(timeZone, now) : null),
    [timeZone, now]
  );

  const fallbackPosition = timeZoneReference || NATION_REFERENCE;
  const fallbackTimeZone = timeZoneReference ? timeZone : NATION_REFERENCE_TIME_ZONE;

  const fallbackDate = useMemo(
    () => getAbarytDate(now, fallbackPosition, fallbackTimeZone),
    [now, fallbackPosition, fallbackTimeZone]
  );

  const date = useMemo(() => {
    if (!position) return fallbackDate;
    return getAbarytDate(now, position, timeZone || null);
  }, [now, position, timeZone, fallbackDate]);

  const basis = position
    ? "precise"
    : timeZoneReference
      ? "timezone"
      : "reference";

  const value = useMemo(
    () => ({
      date,
      fallbackDate,
      status,
      requestLocation,
      continueWithoutLocation,
      usingFallback: basis !== "precise",
      needsLocationConsent: status === "needs-consent",
      basis,
      basisTimeZone: basis === "reference" ? NATION_REFERENCE_TIME_ZONE : timeZone,
      browserTimeZone: timeZone,
    }),
    [
      date,
      fallbackDate,
      status,
      requestLocation,
      continueWithoutLocation,
      basis,
      timeZone,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAbarytDate() {
  const context = useContext(Context);
  if (!context) throw new Error("AbarytDateProvider is required");
  return context;
}
